"use client";

import { useEffect, useState } from "react";
import { cn } from "@/utils/cn";
import Image from "next/image";
import { Title } from "@/components/common/typography/Title";
import type { EventImage } from "@/data/events";

const useColumnCount = () => {
  const [columnCount, setColumnCount] = useState(1);

  useEffect(() => {
    const updateColumnCount = (): void => {
      if (window.innerWidth >= 1024) {
        setColumnCount(3);
      } else if (window.innerWidth >= 768) {
        setColumnCount(2);
      } else {
        setColumnCount(1);
      }
    };

    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, []);

  return columnCount;
};

type GalleryGridProps = {
  title: string[];
  mobileTitle: string[];
  images: EventImage[];
};

export const GalleryGrid = ({
  title,
  mobileTitle,
  images = [],
}: GalleryGridProps) => {
  const columnCount = useColumnCount();

  const getColumnImages = (
    imagesToFilter: EventImage[],
    columnIndex: number,
  ): EventImage[] => {
    if (!imagesToFilter || !Array.isArray(imagesToFilter)) return [];

    // Crée un tableau de colonnes
    const columns: EventImage[][] = Array.from(
      { length: columnCount },
      () => [],
    );

    // Calcule la "hauteur" de chaque colonne (2 pour format 2:3, 1 pour 3:2)
    const getColumnHeight = (col: EventImage[]) =>
      col.reduce((sum, img) => sum + (img.format === "2:3" ? 2 : 1), 0);

    // Distribue chaque image dans la colonne la plus courte
    imagesToFilter.forEach((image) => {
      const shortestColumnIndex = columns
        .map((col, index) => ({ index, height: getColumnHeight(col) }))
        .sort((a, b) => a.height - b.height)[0].index;

      columns[shortestColumnIndex].push(image);
    });

    return columns[columnIndex] || [];
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col items-center justify-center gap-20 px-4 py-20 ph:px-24 md:px-48 lg:px-72 xl:px-96">
      <Title
        text={title}
        position="center"
        color="secondary"
        textPosition="center"
        className="hidden md:flex"
      />
      <Title
        text={mobileTitle}
        position="center"
        textPosition="center"
        color="secondary"
        CustomTextClasses="text-lg xs:text-xl ph:text-2xl"
        className="flex md:hidden"
      />
      <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div className="flex flex-col gap-4">
          {getColumnImages(images, 0).map((image, index) => (
            <div
              key={index}
              className={cn(
                "relative w-full overflow-hidden bg-gray-100",
                image.format === "2:3" ? "aspect-[2/3]" : "aspect-[3/2]",
              )}
            >
              <Image
                src={image.src}
                alt={image.alt}
                fill
                loading="lazy"
                quality={90}
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4">
          {getColumnImages(images, 1).map((image, index) => (
            <div
              key={index}
              className={cn(
                "relative w-full overflow-hidden bg-gray-100",
                image.format === "2:3" ? "aspect-[2/3]" : "aspect-[3/2]",
              )}
            >
              <Image
                src={image.src}
                alt={image.alt}
                fill
                loading="lazy"
                quality={90}
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
            </div>
          ))}
        </div>
        {columnCount === 3 && (
          <div className="flex flex-col gap-4">
            {getColumnImages(images, 2).map((image, index) => (
              <div
                key={index}
                className={cn(
                  "relative w-full overflow-hidden bg-gray-100",
                  image.format === "2:3" ? "aspect-[2/3]" : "aspect-[3/2]",
                )}
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill
                  loading="lazy"
                  quality={90}
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
